/**
 * Centralized image utility functions for handling image URLs, authentication, and error handling
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://backend-api-itt1.onrender.com/api';
const BASE_URL = API_BASE_URL.replace('/api', '');

/**
 * Normalizes image/media URLs from the API
 * Handles various URL formats:
 * - Full URLs (http:// or https://)
 * - Relative paths starting with /
 * - Paths without leading slash
 * - Double base URL issues
 * 
 * @param {string} rawPath - The raw path from the API
 * @returns {string} - Normalized full URL
 */
export const getMediaUrl = (rawPath) => {
  if (!rawPath) return '';

  let path = String(rawPath).trim();

  // If it's already a full URL, fix any double base URLs
  if (path.startsWith('http://') || path.startsWith('https://')) {
    const doubleBase = `${BASE_URL}${BASE_URL}`;
    if (path.includes(doubleBase)) {
      path = path.replace(doubleBase, BASE_URL);
    }
    // Also check for double /api/api
    const doubleApi = `${BASE_URL}/api/api`;
    if (path.includes(doubleApi)) {
      path = path.replace(doubleApi, `${BASE_URL}/api`);
    }
    return path;
  }

  // Remove accidental prefixed API base (with /api) if present
  const apiBase = `${BASE_URL}/api`;
  if (path.startsWith(apiBase)) {
    path = path.slice(apiBase.length);
  }

  // Remove leading /api if present
  if (path.startsWith('/api')) {
    path = path.slice(4);
  }

  // Ensure the path starts with a single leading slash
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }

  return `${BASE_URL}${path}`;
};

/**
 * Gets authentication headers for image requests
 * @returns {Object} - Headers object with Authorization token if available
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Creates an authenticated image URL with token as query parameter
 * This is useful for img src attributes that can't use custom headers
 * 
 * @param {string} rawPath - The raw path from the API
 * @returns {string} - Authenticated image URL
 */
export const getAuthenticatedImageUrl = (rawPath) => {
  const normalizedUrl = getMediaUrl(rawPath);
  if (!normalizedUrl) return '';

  const token = localStorage.getItem('token');
  if (!token) return normalizedUrl;

  // Add token as query parameter for authenticated access
  const separator = normalizedUrl.includes('?') ? '&' : '?';
  return `${normalizedUrl}${separator}token=${encodeURIComponent(token)}`;
};

/**
 * Fetches an image as a blob with authentication
 * Useful for converting images to base64 or handling CORS issues
 * 
 * @param {string} rawPath - The raw path from the API
 * @returns {Promise<Blob|null>} - Image blob or null if failed
 */
export const fetchImageBlob = async (rawPath) => {
  if (!rawPath) return null;

  try {
    const fullUrl = getMediaUrl(rawPath);
    const token = localStorage.getItem('token');
    const isMediaUrl = fullUrl.includes('/media/');

    const response = await fetch(fullUrl, {
      headers: {
        Accept: 'image/*',
        ...(!isMediaUrl && token ? { Authorization: `Bearer ${token}` } : {})
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Image doesn't exist, not an error
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  } catch (error) {
    if (error?.message && !error.message.includes('404')) {
      console.warn('Error fetching image blob:', error.message);
    }
    return null;
  }
};

/**
 * Converts an image URL to base64 with authentication
 * 
 * @param {string} rawPath - The raw path from the API
 * @returns {Promise<string|null>} - Base64 image data URL or null if failed
 */
export const imageToBase64 = async (rawPath) => {
  if (!rawPath) return null;

  try {
    const blob = await fetchImageBlob(rawPath);
    if (!blob) return null;

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        // Check if it's a valid image
        if (base64 && base64.startsWith('data:image')) {
          resolve(base64);
        } else {
          reject(new Error('Invalid image data'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    if (error?.message && !error.message.includes('404')) {
      console.warn('Error converting image to base64:', error.message);
    }
    return null;
  }
};

/**
 * Default fallback image path
 */
export const DEFAULT_FALLBACK_IMAGE = '/assets/images/no_image.png';

